import { Component, type ReactNode } from 'react'
import { Button, Result } from 'antd'

interface Props {
  children: ReactNode
}

interface State {
  hasError: boolean
  error?: Error
}

// 动态 chunk 加载失败（多见于发版后旧页面访问新 chunk）
const CHUNK_ERROR_RE =
  /Failed to fetch dynamically imported module|Loading chunk .* failed|error loading dynamically imported module|importing a module script failed/i

/**
 * 路由级错误边界。
 *
 * 包裹 Dashboard 的 <Outlet/>，任一页面渲染异常或懒加载失败时，只在内容区降级为
 * 友好提示（侧边栏/菜单保持可用），而不是整棵 React 树白屏。配合按 location.pathname
 * 作 key 重挂载，切换菜单即自动恢复，无需手动刷新。
 */
export class RouteErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error }
  }

  componentDidCatch(error: Error, info: unknown) {
    // 保留控制台记录，便于线上排查
    console.error('[RouteErrorBoundary]', error, info)
  }

  private get isChunkError(): boolean {
    return CHUNK_ERROR_RE.test(this.state.error?.message || '')
  }

  private handleReset = () => {
    this.setState({ hasError: false, error: undefined })
  }

  private handleReload = () => {
    window.location.reload()
  }

  render() {
    if (!this.state.hasError) return this.props.children

    if (this.isChunkError) {
      return (
        <Result
          status="info"
          title="检测到新版本"
          subTitle="应用已更新，刷新即可加载最新页面。"
          extra={
            <Button type="primary" onClick={this.handleReload}>
              刷新页面
            </Button>
          }
        />
      )
    }

    return (
      <Result
        status="warning"
        title="页面出现了点问题"
        subTitle="当前页面渲染异常，可点「重试」或刷新；若反复出现请联系管理员。"
        extra={
          <div className="flex items-center justify-center gap-2">
            <Button type="primary" onClick={this.handleReset}>
              重试
            </Button>
            <Button onClick={this.handleReload}>刷新页面</Button>
          </div>
        }
      />
    )
  }
}
